/**
 * The Group and Ungroup commands, plus whether each is currently available.
 *
 * Shared by the overlay's floating button, the Ctrl+G shortcut and the right-click
 * menu, so all three behave identically and none of them owns the logic.
 */

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGroupSelection } from './useGroupSelection';
import { readGroups, createGroup, dissolveGroup, writeGroups } from './groupPersistence';

export const useGroupCommands = () => {
  const currentSlideId = useSelector((s) => s.slide.currentSlideId);
  const slideData = useSelector((s) => currentSlideId && s.slide.slides[currentSlideId]);
  const existingSlides = useSelector((s) => s.slide.slides);
  const mirrorSlides = useSelector((s) => s.slide.mirrorSlides);
  const presentMode = useSelector((s) => s.slide.presentMode);
  const isReadOnly = useSelector((s) => s.slide.isReadOnly);
  const isMasterSlide = useSelector((s) => s.slide.isMasterSlide);
  const dispatch = useDispatch();
  const { id: presentationId } = useParams();

  const { selectedIds, activeGroupId, selectGroup, clear } = useGroupSelection();
  const groups = readGroups(slideData);
  const disabled = presentMode || isReadOnly || isMasterSlide;

  const persistGroups = useCallback((nextGroups) => (
    writeGroups({
      groups: nextGroups,
      currentSlideId,
      currentSlideData: slideData,
      existingSlides,
      mirrorSlides,
      presentationId,
      dispatch,
    })
  ), [currentSlideId, slideData, existingSlides, mirrorSlides, presentationId, dispatch]);

  const canGroup = !disabled && selectedIds.length > 1 && !activeGroupId;
  const canUngroup = !disabled && Boolean(activeGroupId);

  const doGroup = useCallback(() => {
    if (disabled || selectedIds.length < 2) return;
    const { groups: next, groupId } = createGroup(groups, selectedIds);
    persistGroups(next);
    selectGroup(groupId, selectedIds);
  }, [disabled, groups, selectedIds, persistGroups, selectGroup]);

  const doUngroup = useCallback(() => {
    if (disabled || !activeGroupId) return;
    persistGroups(dissolveGroup(groups, activeGroupId));
    clear();
  }, [disabled, groups, activeGroupId, persistGroups, clear]);

  return { doGroup, doUngroup, canGroup, canUngroup, persistGroups, groups };
};

export default useGroupCommands;
